import React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import {
  LayoutDashboard, Calculator, Target, Scale, TrendingUp,
  Bookmark, User, Crown, LogOut, Home, Shield,
} from "lucide-react";

const sideNav = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/shift", label: "Shift Pay", icon: Calculator },
  { to: "/income-goal", label: "Income Goals", icon: Target },
  { to: "/compare", label: "Compare Jobs", icon: Scale },
  { to: "/career", label: "Career Paths", icon: TrendingUp },
  { to: "/saved", label: "Saved", icon: Bookmark },
  { to: "/profile", label: "Profile", icon: User },
];

const bottomNav = [
  { to: "/dashboard", label: "Home", icon: Home },
  { to: "/shift", label: "Calculate", icon: Calculator },
  { to: "/compare", label: "Compare", icon: Scale },
  { to: "/career", label: "Career", icon: TrendingUp },
  { to: "/profile", label: "Profile", icon: User },
];

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isPro = user?.subscription?.plan === "pro" && user?.subscription?.status === "active";

  const doLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Desktop sidebar */}
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 flex-col bg-[#0F2747] px-4 py-6 lg:flex">
        <div className="px-2">
          <Logo light />
        </div>
        <nav className="mt-8 flex-1 space-y-1">
          {sideNav.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={`side-nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-all ${
                  isActive
                    ? "bg-[#18A6A6] text-white shadow-sm"
                    : "text-slate-300 hover:bg-white/5 hover:text-white"
                }`
              }
            >
              <Icon className="h-5 w-5" />
              {label}
            </NavLink>
          ))}
          {user?.role === "admin" && (
            <NavLink
              to="/admin"
              data-testid="side-nav-admin"
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-all ${
                  isActive ? "bg-[#D6A84B] text-[#0F2747]" : "text-[#D6A84B] hover:bg-white/5"
                }`
              }
            >
              <Shield className="h-5 w-5" />
              Admin
            </NavLink>
          )}
        </nav>

        {!isPro ? (
          <button
            data-testid="sidebar-upgrade-btn"
            onClick={() => navigate("/upgrade")}
            className="group mt-4 rounded-xl border border-[#D6A84B]/40 bg-gradient-to-br from-[#16355F] to-[#0F2747] p-4 text-left transition-all hover:border-[#D6A84B]"
          >
            <div className="flex items-center gap-2 text-[#D6A84B]">
              <Crown className="h-4 w-4" />
              <span className="text-xs font-bold uppercase tracking-wider">Go Pro</span>
            </div>
            <p className="mt-1 text-sm font-semibold text-white">Unlock RN Level Up Pro</p>
            <p className="text-xs text-slate-400">$9.99/mo · Save 34% yearly</p>
          </button>
        ) : (
          <div className="mt-4 flex items-center gap-2 rounded-xl bg-[#D6A84B]/15 px-4 py-3 text-[#D6A84B]">
            <Crown className="h-4 w-4" />
            <span className="text-sm font-bold uppercase tracking-wider">Pro Member</span>
          </div>
        )}

        <button
          data-testid="sidebar-logout-btn"
          onClick={doLogout}
          className="mt-3 flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-slate-400 transition-all hover:bg-white/5 hover:text-white"
        >
          <LogOut className="h-5 w-5" />
          Sign out
        </button>
      </aside>

      {/* Mobile top bar */}
      <header className="sticky top-0 z-20 flex items-center justify-between border-b border-slate-200 bg-white/95 px-4 py-3 backdrop-blur-md lg:hidden">
        <Logo />
        {isPro ? (
          <span className="flex items-center gap-1 rounded-full bg-[#D6A84B]/20 px-2.5 py-1 text-xs font-bold uppercase tracking-wider text-[#B8862F]">
            <Crown className="h-3 w-3" /> Pro
          </span>
        ) : (
          <button
            data-testid="mobile-upgrade-btn"
            onClick={() => navigate("/upgrade")}
            className="rounded-full bg-[#0F2747] px-3 py-1.5 text-xs font-semibold text-white"
          >
            Go Pro
          </button>
        )}
      </header>

      {/* Content */}
      <main className="px-4 pb-28 pt-6 sm:px-6 lg:ml-64 lg:px-10 lg:pb-10">
        <div className="mx-auto max-w-6xl">
          <Outlet />
        </div>
      </main>

      {/* Mobile bottom nav */}
      <nav className="fixed inset-x-0 bottom-0 z-30 flex h-16 items-center justify-around border-t border-slate-200 bg-white/95 backdrop-blur-md lg:hidden">
        {bottomNav.map(({ to, label, icon: Icon }) => (
          <NavLink
            key={to}
            to={to}
            data-testid={`bottom-nav-${label.toLowerCase()}`}
            className={({ isActive }) =>
              `flex flex-col items-center gap-0.5 px-3 py-1 text-[11px] font-medium transition-colors ${
                isActive ? "text-[#18A6A6]" : "text-slate-400"
              }`
            }
          >
            <Icon className="h-5 w-5" />
            {label}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
